import * as Yup from "yup";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useWizard } from "components/elements/Wizard/useWizard";
import { useSpeckleModels } from "components/modules/UploadDialog/lib/hooks/useSpeckleModels";
import { nameof } from "lib/actions/util";
import { SpeckleModelModel } from "../../types/SpeckleModelModel";
import {
  FormikHelpers,
  Formik,
  Form,
  Field,
  ErrorMessage,
  useFormikContext,
} from "formik";
import FormError from "components/elements/Forms/FormError";
import FormInput from "components/elements/Forms/FormInput";
import { SpeckleImportModel } from "../../types/SpeckleImportModel";
import { popToastError } from "lib/actions/toast";

export default function SpeckleCreateModelForm(p: {
  speckleProjectId: string;
  onCreated(created: SpeckleModelModel): void;
}) {
  const { values } = useFormikContext<SpeckleImportModel>();
  const speckleConnectedAccountId = parseInt(values.connectedAccountId);
  const { stepViewVariant } = useWizard();
  const { create: createSpeckleModel } = useSpeckleModels(
    speckleConnectedAccountId,
    p.speckleProjectId,
  );

  function onSubmitCreateModel(
    values: FormValues,
    form: FormikHelpers<FormValues>,
  ) {
    createSpeckleModel(values)
      .then((modelResponse) => {
        if (!modelResponse.id || !modelResponse.name) {
          popToastError("Response did not contain expected id and name.");
          return;
        }
        p.onCreated(modelResponse);
      })
      .finally(() => {
        form.setSubmitting(false);
      });
  }

  function onCancel() {
    stepViewVariant(null);
  }

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={onSubmitCreateModel}
      validationSchema={validation}
      validateOnChange
      validateOnBlur
    >
      {({ values, isSubmitting, handleSubmit, submitForm }) => (
        <Form className="text-sm" onSubmit={handleSubmit}>
          <div className="mt-4 space-y-2">
            <label htmlFor="field_name">Name</label>
            <Field
              id="field_name"
              name={nameof(values, "name")}
              as={FormInput}
              className="bg-white"
              disabled={isSubmitting}
              autoComplete="off"
            />
            <ErrorMessage name={nameof(values, "name")} component={FormError} />
          </div>
          <div className="mt-4 space-y-2">
            <label htmlFor="field_description">Description</label>
            <Field
              id="field_description"
              name={nameof(values, "description")}
              as={FormInput}
              className="bg-white"
              disabled={isSubmitting}
              autoComplete="off"
            />
            <ErrorMessage
              name={nameof(values, "description")}
              component={FormError}
            />
          </div>
          <div className="mt-6 flex flex-row-reverse space-x-2 space-x-reverse">
            <button
              type="submit"
              className="rounded bg-black p-2 px-6 text-white disabled:bg-gray-300"
              disabled={isSubmitting}
              onClick={(ev) => (ev.preventDefault(), submitForm())}
            >
              {!isSubmitting ? "Create" : "Working..."}
            </button>
            <button
              type="reset"
              className="rounded p-2 px-6 disabled:text-gray-300"
              disabled={isSubmitting}
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

type FormValues = Omit<SpeckleModelModel, "id">;

const initialFormValues: FormValues = {
  name: "",
  description: "",
};

const validation = Yup.object().shape({
  name: Yup.string().nullable().required("Required."),
  description: Yup.string().nullable(),
});
